import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Customer, CustomerAccount } from '@inyova/models';
import { cloneDeep } from 'lodash-es';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { BOND_LIST, MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { StrategyService } from '@shared/services/strategy.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-strategy-portfolio',
  templateUrl: './strategy-portfolio.component.html',
  styleUrls: ['./strategy-portfolio.component.scss'],
  standalone: false
})
export class StrategyPortfolioComponent implements OnDestroy, OnInit {
  protected readonly onDestroy$ = new Subject<void>();
  bookConsultation: boolean;
  changeStrategyDisabled = false;
  currentAccount: CustomerAccount;
  customer: Customer;
  discardedWishes: string[] = [];
  femaleLeadership: string;
  flipperBookConsultation$: Observable<ThirdPartyServiceItem>;
  initialDiscardedWishes: string[] = [];
  investmentData: CustomerInvestmentData;
  mobileView = false;
  seenDiscardedWishes: { modalOpened: boolean; redirectUrl: string; discardedWishesDialog: boolean };
  showStrategyChanges = '';
  showRegulationModal = false;
  bondList = BOND_LIST;
  scroll = 0;
  showScroll = false;
  CTALoading = false;

  @Input() step: number;
  @Output() updateTab = new EventEmitter<number>();

  constructor(
    private store: Store,
    public router: Router,
    private trackingService: TrackingService,
    private strategyService: StrategyService
  ) {
    this.mobileView = window.innerWidth < MOBILE_VIEW_TRESHOLD;

    this.store
      .pipe(select(fromAppRoot.selectCustomerAndCurrentInvestAccount), takeUntil(this.onDestroy$))
      .subscribe(({ customer, currentAccountDetail }) => {
        this.customer = customer;
        this.currentAccount = currentAccountDetail;
      });

    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((response) => {
      this.investmentData = response;
    });

    this.store.pipe(select(fromStrategy.selectChangeStrategyDisabled), takeUntil(this.onDestroy$)).subscribe((response) => {
      this.changeStrategyDisabled = response;
    });

    this.store.pipe(select(fromStrategy.selectCustomerDiscardedWish), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.discardedWishes = value;

      // update at onece
      if (!this.initialDiscardedWishes.length) {
        this.initialDiscardedWishes = this.discardedWishes;
      }

      // if we have diff show modal again
      if (this.initialDiscardedWishes.length !== this.discardedWishes.length) {
        this.store.dispatch(StrategyActions.setDiscardedWishesDialog({ modalOpened: false, redirectUrl: '', discardedWishesDialog: false }));
      }
    });

    this.store.pipe(select(fromStrategy.selectDiscardedWishesDialog), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.seenDiscardedWishes = value;
    });
    this.store.pipe(select(fromStrategy.selectPortfolioFemaleLeadership), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.femaleLeadership = value as string;
    });

    this.flipperBookConsultation$ = this.store.select(
      fromAppRoot.selectThirdPartyServices('show_book_consultation')
    ) as Observable<ThirdPartyServiceItem>;
  }

  ngOnInit(): void {
    this.trackingService.trackView('/portfolio');
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  changeStrategy(): void {
    if (this.changeStrategyDisabled === false) {
      void this.router.navigate(['/strategy/adjust']);
    }
  }

  bookModal(value: boolean): void {
    if (value) {
      this.trackingService.trackActivity('[Button] Consultation modal CTA: Open modal');
    }
    this.bookConsultation = value;
  }

  saveStrategy(type: string): void {
    this.showStrategyChanges = type;

    setTimeout(() => {
      this.showStrategyChanges = '';
    }, 3200);

    if (type === 'discard') {
      this.store.dispatch(StrategyActions.discardStrategyChanges());
      this.trackingService.trackActivity('Frontend: Strategy change discarded', true);
    } else {
      this.store.dispatch(StrategyActions.saveStrategyChanges());
      this.trackingService.trackActivity('Frontend: Strategy change saved', true);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: Window }): void {
    if (event.target.innerWidth >= MOBILE_VIEW_TRESHOLD) {
      this.mobileView = false;
    } else {
      this.mobileView = true;
    }
  }

  scrollTop(): void {
    this.trackingService.trackActivity('[Button] Strategy portfolio: scroll back to top');
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  getCTA(): string {
    if (this.currentAccount.step_status === 2) {
      return 'cta_open_account';
    }

    if (this.currentAccount.customer_journey.id_verification_completed) {
      return 'cta_transfer_money';
    }

    // it's step 3 and verification isn't completed
    if (this.customer.app_location === 'ch') {
      return 'cta_verification';
    }

    return 'strategy.cta.signContract';
  }

  navigateTo() {
    if (this.currentAccount.step_status === 2) {
      this.navigateToAccountOpening();
    } else {
      this.navigateToVerification();
    }
  }

  navigateToAccountOpening(): void {
    if (this.currentAccount.kind === 'kid') {
      this.updateKidAccountStep();
      return;
    }

    if (this.customer.app_location === 'ch') {
      if (this.customer.saxo_bank_onboarding_done) {
        this.CTALoading = true;
        this.store.dispatch(StrategyActions.skipSaxoOnboarding());
      } else {
        void this.router.navigateByUrl('/next/saxo-account');
      }
      return;
    }

    void this.router.navigateByUrl('/next/account-opening');
  }

  updateKidAccountStep(): void {
    this.strategyService.updateKidsAccountStep().subscribe((value) => {
      const currentAccount = cloneDeep(this.currentAccount);

      currentAccount.step_status = 3;
      currentAccount.customer_journey.account_data_filled = value.data.attributes.account_data_filled;
      currentAccount.customer_journey.id_verification_completed = value.data.attributes.id_verification_completed;
      this.store.dispatch(AppRootActions.fetchCustomerAccountDetailSuccess({ customerAccountDetail: currentAccount }));

      void this.router.navigate(['next', 'fund']);
    });
  }

  navigateToVerification(): void {
    if (this.currentAccount.customer_journey.id_verification_completed) {
      void this.router.navigateByUrl('/next/fund');
      return;
    }

    void this.router.navigateByUrl('/next/saxo-account/start-verification');
  }

  @HostListener('window:scroll') watchScroll() {
    if (this.showScroll === false) {
      if (window.scrollY < this.scroll) {
        this.showScroll = true;
      } else {
        this.scroll = window.scrollY;
      }
    }

    if (window.scrollY < 300) {
      this.showScroll = false;
    }
  }
}
